import { formatDistanceToNowStrict, Locale } from 'date-fns';
import { addDays } from 'date-fns/addDays';
import { addHours } from 'date-fns/addHours';
import { addMinutes } from 'date-fns/addMinutes';
import { addMonths } from 'date-fns/addMonths';
import { addSeconds } from 'date-fns/addSeconds';
import { addWeeks } from 'date-fns/addWeeks';
import { differenceInCalendarWeeks } from 'date-fns/differenceInCalendarWeeks';
import { endOfMonth } from 'date-fns/endOfMonth';
import { endOfWeek } from 'date-fns/endOfWeek';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { setDay } from 'date-fns/setDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfWeek } from 'date-fns/startOfWeek';
import {
  format,
  toDate,
  getTimezoneOffset,
  toZonedTime,
  fromZonedTime,
  formatInTimeZone,
} from 'date-fns-tz';

import { removeItemAll } from './array';

import { importDateFnsLocales } from '../../../utils/import';
import { LocalTimeZone } from '../../../utils/LocalTimeZone';

type Unit = 'h' | 'm' | 's';

interface FormatOption {
  timeZone?: string;
  locale?: Locale;
}

const excludedLocalesForTimezoneHash: Record<string, string[]> = {
  // There is a formatting issue with en-GB in Asia/Dubai timezone
  // It show GTS instead of GST
  // const formatter = new Intl.DateTimeFormat('en-GB', {
  //   timeZone: 'Asia/Dubai',
  //   timeZoneName: 'short',
  // });
  // const formattedParts = formatter.formatToParts(new Date());
  // const timezonePart = formattedParts.find((part) => part.type === 'timeZoneName')?.value;
  // timezonePart === 'GTS' instead of 'GST'
  'Asia/Dubai': ['en-GB'],
  'Asia/Muscat': ['en-GB'], // Same as Dubai
};

const getExcludedLocalesFromTimezone = (timezone?: string): string[] => {
  return timezone ? excludedLocalesForTimezoneHash[timezone] ?? [] : [];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const importDateLocaleSet = (locale: string, timezone?: string): Promise<Locale>[] => {
  const parts = locale.split('-');
  const lang = parts[0] ?? 'en';
  const region = parts[1]?.toUpperCase().trim();

  // Protect ourselves against language only locales that don't work
  const mainLocaleString = region ? [lang, region].join('-') : lang;
  let orderedLocales = [mainLocaleString, lang];
  switch (lang) {
    case 'ar':
      orderedLocales = removeItemAll(
        orderedLocales.concat(['ar-DZ', 'ar-MA', 'ar-SA', 'ar-TN']),
        'ar'
      );
      break;
    case 'en':
      orderedLocales = removeItemAll(
        orderedLocales.concat([
          'en-GB',
          'en-IN',
          'en-US',
          'en-ZA',
          'en-NZ',
          'en-CA',
          'en-AU',
          'en-IE',
        ]),
        'en'
      );
      break;
    case 'fa':
      orderedLocales = removeItemAll(orderedLocales.concat(['fa-IR']), 'fa');
      break;
    case 'de':
      orderedLocales = orderedLocales.concat(['de-AT']);
      break;
    case 'fr':
      orderedLocales = orderedLocales.concat(['fr-CA', 'fr-CH']);
      break;
    case 'ja':
      orderedLocales = orderedLocales.concat(['ja-Hira']);
      break;
    case 'mr':
      orderedLocales = ['hi'];
      break;
    case 'nl':
      orderedLocales = orderedLocales.concat(['nl-BE']);
      break;
    case 'no':
      orderedLocales = orderedLocales.concat(['nb']);
      break;
    case 'pt':
      orderedLocales = orderedLocales.concat('pt-BR');
      break;
    case 'sr':
      orderedLocales = orderedLocales.concat(['sr-Latn']);
      break;
    case 'zh':
      orderedLocales = mainLocaleString === 'zh-HANT' ? ['zh-HK', 'zh-TW'] : ['zh-CN', 'zh-TW'];
      break;
    case 'it':
      orderedLocales = orderedLocales.concat(['it-CH']);
      break;
  }

  const excludedLocales = getExcludedLocalesFromTimezone(timezone);
  return orderedLocales
    .filter((l) => !excludedLocales.includes(l))
    .map((l) => importDateFnsLocales(l));
};

export async function importDateLocale(locale: string, timezone: string): Promise<Locale> {
  const localePromises = importDateLocaleSet(locale, timezone);
  const fmt = 'PPPPpp zz';
  const date = new EDate(new Date(), timezone);
  let dateString = '';
  let firstGoodLocale: Promise<Locale> | undefined;

  for (const localePromise of localePromises) {
    try {
      const localeImport = await localePromise;
      dateString = date.format(fmt, { timeZone: timezone, locale: localeImport });
      if (!firstGoodLocale) {
        firstGoodLocale = localePromise;
      }
      if (!dateString.includes('GMT')) {
        // Try locales until we have one without GMT (for example EST)
        return localePromise;
      }
    } catch {}
  }

  return localePromises[0];
}

function removeYearFromFormattedDate(dateString: string, locale: string): string {
  const parts = locale.split('-');
  const lang = parts[0] ?? 'en';
  switch (lang) {
    case 'ar':
      return dateString;
    case 'hu':
      dateString = dateString.replace(/\b\d{4}\.\s*\b/, '');
      break;
    case 'ko':
      dateString = dateString.replace(/\b\d{4}\.\b/, '');
      break;
    case 'ja':
      dateString = dateString.replace(/\b\d{4}\/\b/, '');
      break;
    case 'ru':
      dateString = dateString.replace(/\b\d{4}\s*г?\.?/, '');
      break;
    case 'uk':
      dateString = dateString.replace(/\b\d{4}\b(?: р\.)?/, '');
      break;
    case 'vi':
      dateString = dateString.replace(/\s*năm?\s*\b\d{4}\b/, '');
      break;
    case 'zh':
      dateString = dateString.replace(/\b\d{4}\-\b/, '');
      break;
    default:
      dateString = dateString.replace(/[,.-\/]?\s*\b\d{4}\b/, '');
  }
  return dateString;
}

export type DateTime = {
  month: number;
  day: number;
  year: number;
  hour: number;
  min: number;
  sec?: number;
  ms?: number;
};

export class EDate {
  public readonly date: Date;
  public readonly timezone: string;
  public hoursOffset(): number {
    return Math.floor(getTimezoneOffset(this.timezone, this.date) / 1000 / 60 / 60);
  }
  public minsOffset(): number {
    return Math.floor((getTimezoneOffset(this.timezone, this.date) / 1000 / 60) % 60);
  }

  public constructor(date: EDate | string);
  public constructor(date: Date | DateTime | string | number, tz: string);
  public constructor(date?: Date | EDate | number | DateTime | string, tz?: string) {
    if (date && date instanceof EDate) {
      this.date = new Date(date.date);
      this.timezone = date.timezone;
      return;
    }

    if (typeof date === 'string' || typeof date === 'number') {
      let d: Date;

      if (tz) {
        d = toDate(date, { timeZone: tz });
      } else {
        d = new Date(date);
        tz = 'UTC';
      }

      if (isNaN(d.getTime())) {
        // Can't use logger since this is shared between front/backend
        console.error('Invalid date string', { date });
        throw new Error(`Invalid date string ${date}`);
      }
      date = d;
    }

    if (date instanceof Date) {
      if (!tz) {
        throw new Error(
          `Invalid use of EDate - date (${typeof date} - ${date.constructor.name}) tz (${tz})`
        );
      }
      this.date = new Date(date);
      this.timezone = tz;
      return;
    }

    if (typeof date === 'object' && tz && !(date instanceof EDate)) {
      // date is DateTime with hours/mins/secs in tz timezone

      const naiveDate = new Date(
        date.year,
        date.month,
        date.day,
        date.hour,
        date.min,
        date.sec ?? 0,
        date.ms ?? 0
      );

      // No need to convert to UTC first, construct the local time in specified timezone directly
      this.date = naiveDate; // This is the local time not adjusted for timezone; further action might be needed based on how you intend to use `this.date`
      this.timezone = tz;

      // If you need `this.date` to be the UTC equivalent of the provided local time, then convert it here
      // However, this step is crucial only if you're working with the `date` in UTC elsewhere in your class
      this.date = fromZonedTime(naiveDate, tz);

      return;
    }

    throw new Error(
      `Invalid use of EDate - date (${typeof date} - ${date?.constructor.name}) tz (${tz})`
    );
  }

  public static parse(date: string | number, timeZone: string): EDate {
    const d = toDate(date, { timeZone });
    return new EDate(d, timeZone);
  }

  public toDate(): Date {
    return new Date(this.date);
  }

  public getTime(): number {
    return this.date.getTime();
  }

  public toTime(): number {
    return this.date.getTime();
  }

  public getFullYear(): number {
    return parseInt(this.format('yyyy'));
  }

  public getMonth(): number {
    return parseInt(this.format('M')) - 1;
  }

  public getDate(): number {
    return parseInt(this.format('d'));
  }

  public getDay(): number {
    return parseInt(this.format('i')) - 1; // 0 (mon) - 6 (sun);
  }

  public getHours(): number {
    return parseInt(this.format('H'));
  }

  public getMinutes(): number {
    return parseInt(this.format('m'));
  }

  public getSeconds(): number {
    return parseInt(this.format('s'));
  }

  public addMonths(months: number): EDate {
    return new EDate(
      addMonths(
        this.date.getTime() + (LocalTimeZone.minsOffset(this.date) - this.minsOffset()),
        months
      ),
      this.timezone
    );
  }

  public addDays(amount: number): EDate {
    return new EDate(
      addDays(
        this.date.getTime() + (LocalTimeZone.minsOffset(this.date) - this.minsOffset()),
        amount
      ),
      this.timezone
    );
  }

  // weekday: mon = 0, sun = 6;
  public setDay(weekday: number): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    const newDate = setDay(zonedDate, weekday === 6 ? 0 : weekday + 1);
    return new EDate(fromZonedTime(newDate, this.timezone), this.timezone);
  }

  public addHours(hours: number): EDate {
    return new EDate(
      addHours(
        this.date.getTime() + (LocalTimeZone.minsOffset(this.date) - this.minsOffset()),
        hours
      ),
      this.timezone
    );
  }

  public addMinutes(mins: number): EDate {
    return new EDate(
      addMinutes(
        this.date.getTime() + (LocalTimeZone.minsOffset(this.date) - this.minsOffset()),
        mins
      ),
      this.timezone
    );
  }

  public addSeconds(seconds: number): EDate {
    return new EDate(
      addSeconds(
        this.date.getTime() + (LocalTimeZone.minsOffset(this.date) - this.minsOffset()),
        seconds
      ),
      this.timezone
    );
  }

  public subtractMonths(months: number): EDate {
    return this.addMonths(-months);
  }

  public subtractDays(days: number): EDate {
    return this.addDays(-days);
  }

  public subtractHours(hours: number): EDate {
    return this.addHours(-hours);
  }

  public subtractMinutes(mins: number): EDate {
    return this.addMinutes(-mins);
  }

  public set(unit: Unit, value: number): EDate {
    switch (unit) {
      case 'h':
        return this.setHours(value);
      case 'm':
        return this.setMinutes(value);
      case 's':
        return this.setSeconds(value);

      default:
        throw new Error(`Unit is invalid: ${unit}`);
    }
  }

  public tz(timezone: string): EDate {
    return new EDate(this.date, timezone);
  }

  public startOfDay(): EDate {
    return this.setHours(0).setMinutes(0).setSeconds(0);
  }

  public endOfDay(): EDate {
    return this.setHours(23).setMinutes(59).setSeconds(59, 999);
  }

  public startOfWeek(): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    const newDate = startOfWeek(zonedDate);
    return new EDate(fromZonedTime(newDate, this.timezone), this.timezone);
  }

  public endOfWeek(): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    const newDate = endOfWeek(zonedDate);
    return new EDate(fromZonedTime(newDate, this.timezone), this.timezone);
  }

  public startOfMonth(): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    const newDate = startOfMonth(zonedDate);
    return new EDate(fromZonedTime(newDate, this.timezone), this.timezone);
  }

  public endOfMonth(): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    const newDate = endOfMonth(zonedDate);
    return new EDate(fromZonedTime(newDate, this.timezone), this.timezone);
  }

  public setHours(hours: number): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    const newHoursDate = new Date(zonedDate.setHours(hours));
    return new EDate(fromZonedTime(newHoursDate, this.timezone), this.timezone);
  }

  public setMinutes(mins: number): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    if (mins === 60) {
      const newMinsDate = new Date(zonedDate.setMinutes(0));
      return new EDate(fromZonedTime(newMinsDate, this.timezone), this.timezone).addHours(1);
    }
    const newMinsDate = new Date(zonedDate.setMinutes(mins));
    return new EDate(fromZonedTime(newMinsDate, this.timezone), this.timezone);
  }

  public setSeconds(secs: number, ms = 0): EDate {
    return new EDate(
      new Date(new Date(this.date).setSeconds(secs)).setMilliseconds(ms),
      this.timezone
    );
  }

  public setMilliseconds(value: number): EDate {
    return new EDate(new Date(this.date).setMilliseconds(value), this.timezone);
  }

  public setDate(date: number): EDate {
    return new EDate(new Date(this.date).setDate(date), this.timezone);
  }

  public setMonth(month: number): EDate {
    return new EDate(new Date(this.date).setMonth(month), this.timezone);
  }

  public setYear(year: number): EDate {
    return new EDate(new Date(this.date).setFullYear(year), this.timezone);
  }

  public isSameDay(date: EDate): boolean {
    return (
      this.getFullYear() === date.getFullYear() &&
      this.getMonth() === date.getMonth() &&
      this.getDate() === date.getDate()
    );
  }

  public isSameYear(date: EDate): boolean {
    return this.getFullYear() === date.getFullYear();
  }

  public isBefore(date: Date | EDate): boolean {
    if (date instanceof EDate) {
      return isBefore(this.date, date.date);
    }
    return isBefore(this.date, date);
  }

  public isSameOrBefore(date: Date | EDate): boolean {
    return !this.isAfter(date);
  }

  public isAfter(date: Date | EDate): boolean {
    if (date instanceof EDate) {
      return isAfter(this.date, date.date);
    }
    return isAfter(this.date, date);
  }

  public isEqual(date: EDate): boolean {
    return this.date.getTime() === date.date.getTime();
  }

  public isSameOrAfter(date: Date | EDate): boolean {
    return !this.isBefore(date);
  }

  public formatDistanceToNow(options?: {
    addSuffix?: boolean;
    unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
    roundingMethod?: 'floor' | 'ceil' | 'round';
    locale?: Locale;
  }): string {
    return formatDistanceToNowStrict(this.date, options);
  }

  public diffInMinutes(earlierDate: EDate): number {
    return Math.ceil((this.date.getTime() - earlierDate.date.getTime()) / 1000 / 60);
  }

  public diffInSeconds(earlierDate: EDate): number {
    return Math.ceil((this.date.getTime() - earlierDate.date.getTime()) / 1000);
  }

  public format(fmt: string, options?: FormatOption): string {
    // BUG:

    // Format has a bug as we're trying to fake the system into thinkint we're in another timezone

    // Basically if you're trying to format out Mar 27 2022 1:00AM CST (Mexico City)
    // while the browser is in London timezone, this function shifts the date object to be 1AM London time
    // however there is no Mar 27 1AM London time as on exactly that time London jumps clocks to Mar 27 2AM
    // which would be ok IF we were actually trying to print out a time for London but we're faking the system
    // and making it print out a time for Mexico City - which is now the wrong time.

    return formatInTimeZone(this.date, this.timezone, fmt, options);
  }

  public async formatAsync(
    fmt: string,
    locale?: string,
    shouldRemoveYear?: boolean
  ): Promise<string> {
    let dateString = '';
    const localePromises = importDateLocaleSet(locale ?? 'en', this.timezone);
    let firstDateString = '';

    for (const localePromise of localePromises) {
      try {
        const localeImport = await localePromise;
        dateString = this.format(fmt, { timeZone: this.timezone, locale: localeImport });
        if (shouldRemoveYear) {
          dateString = removeYearFromFormattedDate(dateString, locale ?? 'en');
        }
        if (!firstDateString) {
          firstDateString = dateString;
        }
        if (!dateString.includes('GMT')) {
          // Try locales until we have one without GMT (for example EST)
          return dateString;
        }
      } catch {}
    }

    return firstDateString;
  }

  public async formatDistanceToNowAsync(
    locale: string,
    options?: {
      addSuffix?: boolean;
      unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
      roundingMethod?: 'floor' | 'ceil' | 'round';
    }
  ): Promise<string> {
    let dateString = '';
    const localePromises = importDateLocaleSet(locale ?? 'en', this.timezone);
    let firstDateString = '';

    for (const localePromise of localePromises) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const localeImport = await localePromise;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        dateString = formatDistanceToNowStrict(this.date, {
          ...options,
          locale: localeImport,
        });
        if (!firstDateString) {
          firstDateString = dateString;
        }
        if (!dateString.includes('GMT')) {
          // Try locales until we have one with GMT (for example EST)
          return dateString;
        }
      } catch {}
    }

    return firstDateString;
  }

  public formatWeekday(): string {
    return format(this.date, 'EEE', { timeZone: this.timezone }); // Mon -> Sun
  }

  public toISOString(): string {
    return this.date.toISOString();
  }

  public toString(): string {
    return this.toISOString();
  }

  public valueOf(): number {
    return this.date.getTime();
  }

  public addWeeks(amount: number): EDate {
    const zonedDate = new Date(toZonedTime(this.date, this.timezone));
    const newDate = addWeeks(zonedDate, amount);
    return new EDate(fromZonedTime(newDate, this.timezone), this.timezone);
  }

  // weekday: mon = 0, sun = 6;
  protected nthDayOfMonth(nthDay: number, weekday: number): EDate {
    const startDateOfMonth = this.startOfMonth();

    let firstWeekday = startDateOfMonth.setDay(weekday);

    if (firstWeekday.isBefore(startDateOfMonth)) {
      const startNextWeek = startDateOfMonth.startOfWeek().addDays(7);
      firstWeekday = startNextWeek.setDay(weekday);
    }

    const dateFound = firstWeekday.addWeeks(nthDay);
    if (dateFound.getMonth() !== this.getMonth()) {
      return firstWeekday.addWeeks(nthDay - 1);
    }
    return dateFound;
  }

  /**
   * Get date based on the nth of day in month, eg. 1st Sunday of the month
   * nthDay: begin from 0 to 4, 0 is the first, 4 is the last
   * weekday: mon = 0, sun = 6;
   * @param nthDay
   * @param weekday
   * @returns
   */
  public getNthDayOfMonth(nthDay: number, weekday: number): EDate | null {
    const dateOfMonth = this.nthDayOfMonth(nthDay, weekday);
    return dateOfMonth;
  }

  /**
   * Count number of calendar weeks from start date
   * @param date
   * @returns
   */
  protected countCalendarWeeksFromDate(date: Date): number {
    return differenceInCalendarWeeks(this.date, date, { weekStartsOn: 1 });
  }

  /**
   * Check the current week is valid every two weeks
   * @param date
   * @returns
   */
  public isValidEveryTwoWeeks(date: Date | EDate): boolean {
    let startDate: Date;
    if (date instanceof EDate) {
      startDate = date.date;
    } else {
      startDate = date;
    }
    const amount = this.countCalendarWeeksFromDate(startDate);

    if (amount % 2 !== 0) return false;

    return true;
  }
}

export default EDate;
